import { useRef, useState } from "react";
import { adminApi, API } from "@/lib/api";
import { Upload, Loader2, X, Film, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_BASE = API.replace(/\/api$/, "");
const VIDEO_RE = /\.(mp4|webm|mov)(\?|$)/i;

// Pick a SEPARATE hover image OR short video shown when hovering a product card.
export const HoverMediaUpload = ({ image = "", video = "", onChange, testId = "hover-media-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const url = video || image;
  const isVideo = !!video || VIDEO_RE.test(url);

  const handleFile = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi().post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const full = data.url.startsWith("http") ? data.url : `${BACKEND_BASE}${data.url}`;
      if (VIDEO_RE.test(full) || (file.type || "").startsWith("video")) {
        onChange({ image: "", video: full });
      } else {
        onChange({ image: full, video: "" });
      }
      toast.success("Hover media added");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const clear = () => onChange({ image: "", video: "" });

  return (
    <div data-testid={testId}>
      {url ? (
        <div className="relative inline-block border border-gray-200 rounded-md overflow-hidden bg-surface">
          {isVideo ? (
            <video src={url} muted loop autoPlay playsInline className="w-32 h-40 object-cover" data-testid="hover-media-video-preview" />
          ) : (
            <img src={url} alt="Hover media" className="w-32 h-40 object-cover" data-testid="hover-media-image-preview" />
          )}
          <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm flex items-center gap-1">
            {isVideo ? <><Film className="w-2.5 h-2.5" /> Video</> : <><ImageIcon className="w-2.5 h-2.5" /> Image</>}
          </span>
          <button type="button" onClick={clear} data-testid="hover-media-remove"
            className="absolute top-1 right-1 bg-white/90 text-charcoal rounded-full p-0.5 hover:bg-pink hover:text-white transition-colors" aria-label="Remove hover media">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid="hover-media-add-btn"
          className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 py-6 text-gray-500 hover:border-pink hover:text-pink transition-colors disabled:opacity-60">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
          <span className="text-xs uppercase tracking-[0.12em]">{uploading ? "Uploading..." : "Attach Hover Image or Video"}</span>
          <span className="text-[11px] text-gray-400">Image (JPG/PNG/WEBP, 8MB) or short video (MP4/WEBM, 25MB)</span>
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" onChange={handleFile} className="hidden" data-testid="hover-media-file-input" />
    </div>
  );
};
